import React, { useEffect, useRef, useState, useMemo } from 'react'
import {
  GoogleMap,
  Marker,
  DirectionsService,
  DirectionsRenderer,
  useJsApiLoader
} from '@react-google-maps/api'
import dropIcon from '../../dropMarker.png'
import bike from '../../provider.png'
import { sendLocation } from '../Utils/utils'

const Mapview = props => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyDON2ssiQZahfBJ0IrfePcEl39FU2cfFRI'
  })

  const [currentPos, setCurrentPos] = useState(null)
  const [directions, setDirections] = useState(null)
  const [bikePos, setBikePos] = useState(null)
  const [mapRef, setMapRef] = useState(null)
  const posRef = useRef(null)
  const prevCenter = useRef(null)

  const drop = props?.destination
    ? {
        lat: Number(props?.destination?.lat),
        lng: Number(props?.destination?.lng)
      }
    : null

  const containerStyle = useMemo(
    () => ({
      width: '100%',
      height: '50vh'
    }),
    []
  )

  const mapOptions = useMemo(
    () => ({
      minZoom: 10,
      maxZoom: 18,
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: window.google?.maps.ControlPosition.TOP_RIGHT // optional
      }
    }),
    []
  )

  // 🔹 Watch live location + fallback polling for iOS throttling
  useEffect(() => {
    let watchId
    if ('geolocation' in navigator) {
      watchId = navigator.geolocation.watchPosition(
        pos => {
          setCurrentPos({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          })
          console.log('hi')
        },
        err => {
          console.error('❌ Geolocation error:', err)
          alert('Please allow location access!')
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
      )

      // Fallback polling every 10s
      const interval = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          pos => {
            setCurrentPos({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude
            })
          },
          err => console.log('❌ getCurrentPosition error:', err),
          { enableHighAccuracy: true }
        )
      }, 10000)

      return () => {
        if (watchId) navigator.geolocation.clearWatch(watchId)
        clearInterval(interval)
      }
    } else {
      alert('Geolocation not supported in this browser.')
    }
  }, [])

  // 🔹 Keep latest position in ref
  useEffect(() => {
    if (currentPos) {
      posRef.current = currentPos
    }
  }, [currentPos])

  // 🔹 Send location to server every 10s
  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (!posRef.current) return
      try {
        const { lat, lng } = posRef.current
        const response = await sendLocation(props?.id, lat, lng)
        console.log('📡 Location sent:', response?.data?.current_location)
      } catch (error) {
        console.error('Send location error:', error)
      }
    }, 10000)

    return () => clearInterval(intervalId)
  }, [])

  // 🔹 Animate bike along route once directions are loaded
  useEffect(() => {
    if (directions) {
      const route = directions.routes[0].overview_path
      let index = 0

      const interval = setInterval(() => {
        if (index >= route.length) {
          clearInterval(interval)
          return
        }
        setBikePos({
          lat: route[index].lat(),
          lng: route[index].lng()
        })
        index++
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [directions])

  // 🔹 Only trigger directions once (don't reset every time currentPos changes)
  useEffect(() => {
    if (currentPos && drop && !directions) {
      setDirections(null) // allow DirectionsService to run once
    }
  }, [currentPos, drop, directions])

  // 🔹 Pan map smoothly (only if moved significantly)
  useEffect(() => {
    if (mapRef && currentPos) {
      if (!prevCenter.current) {
        mapRef.panTo(currentPos)
        prevCenter.current = currentPos
      } else {
        const dist = Math.sqrt(
          Math.pow(currentPos.lat - prevCenter.current.lat, 2) +
            Math.pow(currentPos.lng - prevCenter.current.lng, 2)
        )
        if (dist > 0.0005) {
          mapRef.panTo(currentPos)
          prevCenter.current = currentPos
        }
      }
    }
  }, [currentPos, mapRef])

  if (!isLoaded) return <div>Loading...</div>
  if (!currentPos) return <div>Requesting location permission...</div>

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      zoom={18}
      onLoad={map => setMapRef(map)}
      options={mapOptions}
    >
      {/* Rider Marker */}
      {currentPos && (
        <Marker
          position={currentPos}
          icon={{
            url: bike,
            scaledSize: new window.google.maps.Size(60, 60),
            anchor: new window.google.maps.Point(30, 30)
          }}
        />
      )}

      {/* Drop Marker */}
      {drop && (
        <Marker
          position={drop}
          icon={{
            url: dropIcon,
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20)
          }}
        />
      )}

      {/* Directions Service */}
      {!directions && currentPos && drop && (
        <DirectionsService
          options={{
            origin: currentPos,
            destination: drop,
            travelMode: 'DRIVING'
          }}
          callback={(res, status) => {
            if (res !== null && status === 'OK') {
              setDirections(res)
            }
          }}
        />
      )}

      {/* Render Route */}
      {directions && (
        <DirectionsRenderer
          directions={directions}
          options={{
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: '#000000',
              strokeWeight: 5,
              strokeOpacity: 1.0
            }
          }}
        />
      )}
    </GoogleMap>
  )
}

export default Mapview
